import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Buildings, Stethoscope, CurrencyInr, SignOut } from "@phosphor-icons/react";

export default function HospitalPortal() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [doctors, setDoctors] = useState([]);
  const [summary, setSummary] = useState(null);
  const [hospitals, setHospitals] = useState([]);

  useEffect(() => {
    api.get("/hospital/doctors").then((r) => setDoctors(r.data));
    api.get("/hospital/summary").then((r) => setSummary(r.data));
    api.get("/hospitals").then((r) => setHospitals(r.data));
  }, []);

  return (
    <div className="min-h-screen bg-bone flex">
      <aside className="hidden md:flex w-60 flex-col bg-charcoal text-bone">
        <div className="p-6 border-b border-white/10">
          <div className="font-clinical text-xl">Gutvibe</div>
          <div className="overline text-terracotta text-[10px] mt-1">Hospital Portal</div>
        </div>
        <nav className="p-3 flex-1 space-y-1">
          <Link to="/hospital" className="block px-4 py-2 rounded-md bg-white/10 text-sm">Overview</Link>
        </nav>
        <div className="p-4 border-t border-white/10">
          <div className="text-sm mb-2">{user?.name}</div>
          <Button size="sm" onClick={() => { logout(); nav("/"); }} className="w-full bg-white/10 hover:bg-white/20 text-white border-0" data-testid="hospital-logout">
            <SignOut size={14} className="mr-1" /> Sign out
          </Button>
        </div>
      </aside>

      <main className="flex-1 p-6 md:p-10">
        <div className="mb-8">
          <div className="overline text-terracotta">Partner operations</div>
          <h1 className="font-clinical text-4xl text-charcoal mt-1">Hospital Portal</h1>
        </div>

        {summary && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
            <Card className="border-black/5 bg-white rounded-md"><CardContent className="p-4"><Stethoscope size={16} className="text-ayurveda mb-1" /><div className="overline text-[10px] text-charcoal/50">Doctors</div><div className="font-clinical text-2xl">{summary.doctors}</div></CardContent></Card>
            <Card className="border-black/5 bg-white rounded-md"><CardContent className="p-4"><Buildings size={16} className="text-ayurveda mb-1" /><div className="overline text-[10px] text-charcoal/50">Hospitals</div><div className="font-clinical text-2xl">{summary.hospitals}</div></CardContent></Card>
            <Card className="border-black/5 bg-white rounded-md"><CardContent className="p-4"><div className="overline text-[10px] text-charcoal/50">Appointments</div><div className="font-clinical text-2xl">{summary.appointments}</div></CardContent></Card>
            <Card className="border-black/5 bg-white rounded-md"><CardContent className="p-4"><CurrencyInr size={16} className="text-terracotta mb-1" /><div className="overline text-[10px] text-charcoal/50">Paid appts</div><div className="font-clinical text-2xl text-terracotta">{summary.paid_appointments}</div></CardContent></Card>
          </div>
        )}

        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <div className="overline text-charcoal/50 mb-3">Empanelled doctors</div>
            <div className="space-y-2">
              {doctors.map((d) => (
                <Card key={d.id} className="border-black/5 bg-white rounded-md">
                  <CardContent className="p-4 flex items-center gap-3">
                    <img src={d.photo_url} alt={d.name} className="w-12 h-12 rounded-full object-cover" />
                    <div className="flex-1">
                      <div className="font-clinical text-charcoal">{d.name}</div>
                      <div className="text-xs text-charcoal/60">{d.specialty} · {d.experience_years} yrs · Reg {d.registration_no}</div>
                    </div>
                    <Badge className="bg-ayurveda text-bone">₹{d.fee}</Badge>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
          <div>
            <div className="overline text-charcoal/50 mb-3">Partner hospitals</div>
            <div className="space-y-2">
              {hospitals.map((h) => (
                <Card key={h.id} className="border-black/5 bg-white rounded-md">
                  <CardContent className="p-4">
                    <div className="font-clinical text-charcoal">{h.name}</div>
                    <div className="text-xs text-charcoal/60">{h.city}, {h.state} · {h.phone}</div>
                    <div className="flex flex-wrap gap-1 mt-2">
                      {h.emergency_capable && <Badge className="bg-terracotta text-white text-[10px]">Emergency 24×7</Badge>}
                      {h.specialties?.map((s) => <Badge key={s} variant="outline" className="text-[10px]">{s}</Badge>)}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
